import { ThumbsUp, ThumbsDown } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { toast } from "@/utils/toast";
import { Button } from "@/components/button/Button";
import { Toggle } from "@/components/ui/toggle";
import { Tooltip } from "@/components/tooltip/tooltip";
import { useFeedback, type FeedbackRating } from "@/hooks/useFeedback";
import type { BookDetailsArea, FeedbackContentType } from "@/types/feedback";
import { FeedbackCategoryChips } from "./FeedbackCategoryChips";

const BOOK_DETAILS_AREAS: readonly BookDetailsArea[] = [
  "information",
  "genre",
  "world_settings",
];

const AREA_LABEL_KEY: Record<BookDetailsArea, string> = {
  information: "feedback.areas.information",
  genre: "feedback.areas.genre",
  world_settings: "feedback.areas.worldSettings",
};

interface FeedbackControlsProps {
  jobId: string;
  contentType: FeedbackContentType;
  promptKey?: string;
  sectionIndex?: number | null;
  showExpandedBackground?: boolean;
}

export function FeedbackControls({
  jobId,
  contentType,
  promptKey,
  sectionIndex = null,
  showExpandedBackground = false,
}: FeedbackControlsProps) {
  const { t } = useTranslation("jobDetails");
  const {
    rating,
    categories,
    area,
    comment,
    isExpanded,
    isSubmitting,
    handleRate,
    handleToggleCategory,
    handleSelectArea,
    handleCommentChange,
    handleSubmit,
    handleCancel,
  } = useFeedback({ jobId, contentType, sectionIndex });

  const expandedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isExpanded && expandedRef.current) {
      const firstFocusable = expandedRef.current.querySelector<HTMLElement>(
        "input, textarea, button",
      );
      firstFocusable?.focus();
    }
  }, [isExpanded]);

  const onSubmit = async () => {
    const ok = await handleSubmit();
    if (ok) {
      toast.success(t("feedback.thanks"));
    } else {
      toast.error(t("feedback.submitFailed"));
    }
  };

  const isBookDetails = contentType === "book_details";

  const expandedLeftLabel = isExpanded
    ? rating === "negative"
      ? isBookDetails
        ? t("feedback.areaPrompt")
        : t("feedback.whatWentWrong")
      : null
    : null;

  const renderThumb = (value: FeedbackRating) => {
    const Icon = value === "positive" ? ThumbsUp : ThumbsDown;
    const tooltipKey =
      value === "positive" ? "feedback.helpful" : "feedback.notHelpful";
    const isOn = rating === value;
    const colorClasses =
      value === "positive"
        ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-100 data-[state=on]:bg-emerald-50 data-[state=on]:text-emerald-600"
        : "bg-rose-50 text-rose-500 hover:bg-rose-100 data-[state=on]:bg-rose-50 data-[state=on]:text-rose-500";
    return (
      <Tooltip content={t(tooltipKey)}>
        <Toggle
          pressed={isOn}
          onPressedChange={() => handleRate(value)}
          size="sm"
          className={cn(
            "rounded-md p-0 h-7 w-7",
            isOn ? colorClasses : "text-slate-400 hover:text-slate-600",
          )}
          aria-label={t(tooltipKey)}
          data-testid={`feedback-thumbs-${value === "positive" ? "up" : "down"}`}
        >
          <Icon className={cn("w-4 h-4", isOn && "fill-current")} />
        </Toggle>
      </Tooltip>
    );
  };

  const commentLabelKey =
    rating === "positive"
      ? "feedback.commentLabel.positive"
      : "feedback.commentLabel.negative";
  const commentPlaceholderKey =
    rating === "positive"
      ? "feedback.commentPlaceholder.positive"
      : "feedback.commentPlaceholder.negative";

  return (
    <div
      className={cn(
        showExpandedBackground &&
          isExpanded &&
          "-mx-4 px-4 pt-3 pb-4 rounded-2xl bg-secondary-50/75 transition-colors duration-200",
      )}
      data-testid={`feedback-controls-${contentType}`}
    >
      <div className="flex items-center justify-between gap-2">
        {expandedLeftLabel && (
          <span className="text-sm font-medium text-foreground">
            {expandedLeftLabel}
          </span>
        )}
        <div
          className={cn(
            "flex items-center gap-2",
            !expandedLeftLabel && "ml-auto",
          )}
        >
          {promptKey && (
            <span className="text-sm text-secondary-500 mr-1">
              {t(promptKey)}
            </span>
          )}
          <div
            role="group"
            aria-label={t("feedback.rateContent")}
            className="flex items-center gap-0.5"
          >
            {renderThumb("positive")}
            {renderThumb("negative")}
          </div>
        </div>
      </div>

      <div
        className={cn(
          "overflow-hidden transition-all duration-200 ease-out",
          isExpanded ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0",
        )}
        role="region"
        aria-label={t("feedback.detailsRegionLabel")}
        aria-expanded={isExpanded}
        aria-hidden={!isExpanded}
        inert={!isExpanded}
      >
        <div ref={expandedRef} className="pt-0.75 space-y-4">
          {isBookDetails && rating === "negative" && (
            <div
              className="flex flex-wrap gap-2 max-w-[80%]"
              role="group"
              aria-label={t("feedback.areaPrompt")}
            >
              {BOOK_DETAILS_AREAS.map((a) => (
                <Toggle
                  key={a}
                  pressed={area === a}
                  onPressedChange={() => handleSelectArea(a)}
                  size="sm"
                  className={cn(
                    "rounded-full px-3 h-7 text-xs font-medium border transition-colors",
                    area === a
                      ? "!bg-white !border-primary-500 !text-primary-500 data-[state=on]:!bg-white data-[state=on]:text-primary-500"
                      : "!bg-white border-slate-200 text-secondary-600 hover:border-slate-300 hover:!bg-white",
                  )}
                  data-testid={`feedback-area-${a}`}
                >
                  {t(AREA_LABEL_KEY[a])}
                </Toggle>
              ))}
            </div>
          )}

          {rating === "negative" && (
            <FeedbackCategoryChips
              contentType={contentType}
              selected={categories}
              onToggle={handleToggleCategory}
              hideLegend={!isBookDetails}
            />
          )}

          <div>
            <label
              htmlFor={`feedback-comment-${contentType}`}
              className="text-sm font-medium text-foreground block mb-2"
            >
              {t(commentLabelKey)}
              <span className="text-secondary-500 font-normal ml-1">
                {t("feedback.optional")}
              </span>
            </label>
            <textarea
              id={`feedback-comment-${contentType}`}
              value={comment}
              onChange={(e) => handleCommentChange(e.target.value)}
              placeholder={t(commentPlaceholderKey)}
              rows={3}
              className={cn(
                "w-full rounded-xl border border-slate-200 bg-white px-3 py-2",
                "text-sm text-foreground placeholder:text-slate-400",
                "focus:outline-none focus:border-primary-500",
                "resize-y transition-colors",
              )}
              aria-required="false"
              data-testid={`feedback-comment-${contentType}`}
            />
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              testId={`feedback-cancel-${contentType}`}
            >
              {t("feedback.cancel")}
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={onSubmit}
              loading={isSubmitting}
              testId={`feedback-submit-${contentType}`}
            >
              {t("feedback.submit")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
